// webapp/src/pages/Overdue.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

const { Text, Link } = Typography;

function getTelegramInitData() {
  try {
    return window?.Telegram?.WebApp?.initData || "";
  } catch {
    return "";
  }
}

async function apiFetch(path, { method = "GET", body, query } = {}) {
  const initData = getTelegramInitData();
  const qs =
    query && Object.keys(query).length
      ? "?" +
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && String(v) !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";

  const res = await fetch(`/api${path}${qs}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(initData ? { "x-telegram-init-data": initData } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // ignore
  }

  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function clamp(s, n = 140) {
  const t = String(s || "");
  if (t.length <= n) return t;
  return t.slice(0, n - 1) + "…";
}

function fmtDuration(sec) {
  const s = Math.max(0, Number(sec || 0));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  const mm = m % 60;
  const ss = s % 60;

  if (d > 0) return `${d}д ${h % 24}ч`;
  if (h > 0) return `${h}ч ${mm}м`;
  if (m > 0) return `${m}м ${ss}с`;
  return `${ss}с`;
}

function fmtTs(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (x) => String(x).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function getOverdueTone(overdueSec) {
  const s = Math.max(0, Number(overdueSec || 0));
  if (s >= 3600) return { color: "red", label: "Critical" };
  if (s >= 1800) return { color: "volcano", label: "High" };
  if (s >= 600) return { color: "gold", label: "Medium" };
  return { color: "blue", label: "Low" };
}

function getQuickFilterCount(items, nowTs, minSec) {
  return (items || []).filter((x) => {
    const due = Number(x?.due_ts || 0);
    if (!due || due > nowTs) return false;
    const overdueSec = Math.max(0, nowTs - due);
    return overdueSec >= minSec;
  }).length;
}

function statusTag(status) {
  const s = String(status || "unknown");

  const map = {
    pending: { text: "Ожидает", color: "default", icon: <ClockCircleOutlined /> },
    alerted: { text: "Просрочено", color: "orange", icon: <WarningOutlined /> },
    in_progress: { text: "В работе", color: "blue", icon: null },
    in_progress_alerted: { text: "В работе (просрочено)", color: "volcano", icon: <WarningOutlined /> },
    answered: { text: "Закрыто", color: "green", icon: null },
    unknown: { text: "Неизвестно", color: "default", icon: null },
  };

  const it = map[s];
  if (!it) return <Tag>{s}</Tag>;

  return (
    <Tag color={it.color} icon={it.icon || undefined}>
      {it.text}
    </Tag>
  );
}

function buildTmeLink(chatId, messageId) {
  const cid = String(chatId || "");
  const mid = Number(messageId || 0);
  if (!cid || !mid) return null;

  // Для супергруппы: chat_id обычно "-1001234567890"
  // Ссылка: https://t.me/c/1234567890/42  (без "-100")
  if (cid.startsWith("-100")) {
    const internal = cid.slice(4);
    if (!internal) return null;
    return `https://t.me/c/${internal}/${mid}`;
  }

  // Для обычных групп/лички ссылку на конкретное сообщение не построить универсально
  return null;
}

const STAR_OPTIONS = [
  { value: "all", label: "Все звёзды" },
  ...Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: `⭐ ${i + 1}`,
  })),
];

const STATUS_OPTIONS = [
  { value: "open", label: "Открытые (все, кроме закрытых)" },
  { value: "pending", label: "Ожидает (pending)" },
  { value: "alerted", label: "Просрочено (alerted)" },
  { value: "in_progress", label: "В работе (in_progress)" },
  { value: "in_progress_alerted", label: "В работе (просрочено)" },
  { value: "answered", label: "Закрыто (answered)" },
  { value: "all", label: "Все" },
];

const PERIOD_OPTIONS = [
  { value: 24, label: "24 часа" },
  { value: 24 * 3, label: "3 дня" },
  { value: 24 * 7, label: "7 дней" },
  { value: 24 * 30, label: "30 дней" },
];

const metricCardStyle = {
  borderRadius: 16,
  border: "1px solid #f0f0f0",
  boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
};

const panelCardStyle = {
  borderRadius: 16,
  border: "1px solid #f0f0f0",
  boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
};

const filterLabelStyle = {
  display: "block",
  marginBottom: 6,
};

export default function Overdue() {
  const [activeTab, setActiveTab] = useState("list");

  // Filters
  const [star, setStar] = useState("all");
  const [status, setStatus] = useState("open");
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  // Words filters
  const [hours, setHours] = useState(24);
  const [range, setRange] = useState(null); // DatePicker.RangePicker
  const [minLen, setMinLen] = useState(3);
  const [top, setTop] = useState(30);

  // Data
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const [items, setItems] = useState([]);

  const [loadingWords, setLoadingWords] = useState(false);
  const [wordsError, setWordsError] = useState(null);
  const [words, setWords] = useState([]);

  const nowTs = Math.floor(Date.now() / 1000);

  const debouncedSetSearch = useMemo(() => {
    let t = null;
    return (v) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => setSearch(v), 250);
    };
  }, []);

  const listQuery = useMemo(() => {
    const q = {
      star,
      status,
      limit,
      search: search.trim() || undefined,
    };
    return q;
  }, [star, status, limit, search]);

  const wordsQuery = useMemo(() => {
    // Если указан range — считаем from/to
    let from = undefined;
    let to = undefined;
    if (Array.isArray(range) && range[0] && range[1]) {
      const a = range[0].toDate ? range[0].toDate() : new Date(range[0]);
      const b = range[1].toDate ? range[1].toDate() : new Date(range[1]);
      from = Math.floor(a.getTime() / 1000);
      to = Math.floor(b.getTime() / 1000);
    }

    const q = {
      hours: range ? undefined : hours,
      from,
      to,
      star,
      top,
      min_len: minLen,
    };
    return q;
  }, [range, hours, star, top, minLen]);

  async function loadList() {
    setLoadingList(true);
    setListError(null);
    try {
      // Ожидаем, что на бэке будет /api/sla/overdue
      const data = await apiFetch("/sla/overdue", { query: listQuery });

      // Поддерживаем разные форматы ответа, чтобы не получать «пусто» при рабочем API
      const arr =
        (Array.isArray(data?.items) && data.items) ||
        (Array.isArray(data?.rows) && data.rows) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.sla) && data.sla) ||
        (Array.isArray(data?.result) && data.result) ||
        [];

      setItems(arr);
    } catch (e) {
      setItems([]);
      setListError(e);
    } finally {
      setLoadingList(false);
    }
  }

  async function loadWords() {
    setLoadingWords(true);
    setWordsError(null);
    try {
      // Ожидаем, что на бэке будет /api/sla/overdue/words
      const data = await apiFetch("/sla/overdue/words", { query: wordsQuery });

      // Поддерживаем разные форматы ответа
      const arr =
        (Array.isArray(data?.top_words) && data.top_words) ||
        (Array.isArray(data?.topWords) && data.topWords) ||
        (Array.isArray(data?.items) && data.items) ||
        (Array.isArray(data?.words) && data.words) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.result) && data.result) ||
        [];

      setWords(arr);
    } catch (e) {
      setWords([]);
      setWordsError(e);
    } finally {
      setLoadingWords(false);
    }
  }

  useEffect(() => {
    // Подгружаем список при входе и изменении фильтров
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [star, status, limit, search]);

  useEffect(() => {
    if (activeTab === "analysis") loadWords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, wordsQuery]);

  const metrics = useMemo(() => {
    const open = (items || []).filter((x) => String(x.status || "") !== "answered");
    const overdue = open.filter((x) => x?.due_ts && Number(x.due_ts) <= nowTs);
    const overdueSecList = overdue
      .map((x) => nowTs - Number(x.due_ts || nowTs))
      .filter((n) => Number.isFinite(n) && n >= 0);

    const avgOverdueSec =
      overdueSecList.length > 0
        ? Math.round(overdueSecList.reduce((a, b) => a + b, 0) / overdueSecList.length)
        : 0;

    const maxOverdueSec = overdueSecList.length ? Math.max(...overdueSecList) : 0;

    return {
      openCount: open.length,
      overdueCount: overdue.length,
      avgOverdueSec,
      maxOverdueSec,
    };
  }, [items, nowTs]);

  const quickMetrics = useMemo(() => {
    const criticalCount = getQuickFilterCount(items, nowTs, 3600);
    const highCount = getQuickFilterCount(items, nowTs, 1800);
    const mediumCount = getQuickFilterCount(items, nowTs, 600);
    return { criticalCount, highCount, mediumCount };
  }, [items, nowTs]);

  const columns = [
    {
      title: "⭐",
      dataIndex: "star_level",
      key: "star_level",
      width: 70,
      render: (v) => <Text>{v ? `⭐${v}` : "—"}</Text>,
      sorter: (a, b) => Number(a.star_level || 0) - Number(b.star_level || 0),
    },
    {
      title: "Чат",
      dataIndex: "client_chat_title",
      key: "client_chat_title",
      render: (_v, r) => {
        const title = r?.client_chat_title || "Без названия";
        const chatId = String(r?.source_chat_id || "");
        return (
          <Space direction="vertical" size={2}>
            <Text strong>{title}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              chat_id: {chatId || "—"}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Клиент",
      key: "client",
      width: 220,
      render: (_v, r) => {
        const name = r?.incoming_from_name ? String(r.incoming_from_name) : "";
        const uname = r?.incoming_from_username ? String(r.incoming_from_username) : "";
        const fromId = r?.incoming_from_id ? String(r.incoming_from_id) : "";
        return (
          <Space direction="vertical" size={2}>
            <Text>{name || (uname ? `@${uname}` : "—")}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {uname ? `@${uname}` : fromId ? `id: ${fromId}` : "—"}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 190,
      render: (v) => statusTag(v),
      filters: STATUS_OPTIONS.filter((x) => !["open", "all"].includes(x.value)).map((x) => ({
        text: x.label.replace(/\s*\(.*\)\s*$/, ""),
        value: x.value,
      })),
      onFilter: (val, rec) => String(rec.status || "") === String(val),
    },
    {
      title: "Просрочка",
      key: "overdue",
      width: 190,
      defaultSortOrder: "descend",
      render: (_v, r) => {
        const due = Number(r?.due_ts || 0);
        if (!due) return <Text type="secondary">—</Text>;
        const overdueSec = Math.max(0, nowTs - due);
        const isOverdue = due <= nowTs;
        const tone = getOverdueTone(overdueSec);
        return (
          <Space direction="vertical" size={4}>
            <Space wrap size={6}>
              <Badge status={isOverdue ? "error" : "success"} />
              <Tag color={isOverdue ? tone.color : "green"}>
                {isOverdue ? fmtDuration(overdueSec) : "в пределах"}
              </Tag>
              {isOverdue ? <Tag color={tone.color}>{tone.label}</Tag> : null}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              due: {fmtTs(due)}
            </Text>
          </Space>
        );
      },
      sorter: (a, b) => Number(a?.due_ts || 0) - Number(b?.due_ts || 0),
    },
    {
      title: "Сообщение",
      dataIndex: "incoming_text",
      key: "incoming_text",
      render: (v, r) => {
        const t = String(v || "");
        const link = buildTmeLink(r?.source_chat_id, r?.incoming_message_id);
        return (
          <Space direction="vertical" size={4} style={{ maxWidth: 520 }}>
            <Tooltip title={t || "—"}>
              <Text style={{ cursor: "help" }}>{clamp(t, 160) || <Text type="secondary">—</Text>}</Text>
            </Tooltip>
            <Space size={10} wrap>
              {link ? (
                <Link href={link} target="_blank" rel="noreferrer">
                  Открыть сообщение
                </Link>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  (ссылка недоступна)
                </Text>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                msg_id: {r?.incoming_message_id ?? "—"}
              </Text>
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Действия",
      key: "actions",
      width: 220,
      render: (_v, r) => {
        const id = r?.id;

        const call = async (path) => {
          try {
            await apiFetch(`/sla/${id}/${path}`, { method: "POST" });
            message.success("Готово");
            loadList();
          } catch (e) {
            message.error(e?.message || "Ошибка");
          }
        };

        return (
          <Space wrap>
            <Button size="small" type="default" onClick={() => call("mute")}>
              🔕 Mute
            </Button>

            <Button size="small" type="default" onClick={() => call("unmute")}>
              🔔 Unmute
            </Button>

            <Button size="small" type="primary" onClick={() => call("close")}>
              ✅ Закрыть
            </Button>

            <Button size="small" danger onClick={() => call("reset-alerts")}>
              ♻️ Сброс
            </Button>
          </Space>
        );
      },
    },
  ];

  const expandedRowRender = (r) => {
    const fullText = String(r?.incoming_text || "");
    const link = buildTmeLink(r?.source_chat_id, r?.incoming_message_id);
    return (
      <div style={{ padding: 8 }}>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Space wrap>
            <Tag>sla_id: {r?.id ?? "—"}</Tag>
            <Tag>chat_id: {String(r?.source_chat_id || "—")}</Tag>
            <Tag>msg_id: {r?.incoming_message_id ?? "—"}</Tag>
            <Tag>incoming: {fmtTs(r?.incoming_ts)}</Tag>
            {r?.alerted_ts ? <Tag color="orange">alerted: {fmtTs(r?.alerted_ts)}</Tag> : null}
            {r?.answered_ts ? <Tag color="green">answered: {fmtTs(r?.answered_ts)}</Tag> : null}
          </Space>

          {link ? (
            <Link href={link} target="_blank" rel="noreferrer">
              Открыть исходное сообщение клиента
            </Link>
          ) : null}

          <Card size="small" style={{ borderRadius: 12 }}>
            <Text style={{ whiteSpace: "pre-wrap" }}>{fullText || "—"}</Text>
          </Card>
        </Space>
      </div>
    );
  };

  const listTopAlert = useMemo(() => {
    if (!listError) return null;
    const statusCode = listError?.status;
    const hint =
      statusCode === 401
        ? "Откройте Mini App внутри Telegram (нужен initData)."
        : statusCode === 404
        ? "Эндпоинт /api/sla/overdue ещё не добавлен на сервере."
        : "Проверьте бэкенд и логи API.";
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить просрочки"
        description={
          <Space direction="vertical" size={4}>
            <Text>
              {String(listError?.message || "error")}
              {statusCode ? ` (HTTP ${statusCode})` : ""}
            </Text>
            <Text type="secondary">{hint}</Text>
          </Space>
        }
      />
    );
  }, [listError]);

  const wordsTopAlert = useMemo(() => {
    if (!wordsError) return null;
    const statusCode = wordsError?.status;
    const hint =
      statusCode === 401
        ? "Откройте Mini App внутри Telegram (нужен initData)."
        : statusCode === 404
        ? "Эндпоинт /api/sla/overdue/words ещё не добавлен на сервере."
        : "Проверьте бэкенд и логи API.";
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить аналитику"
        description={
          <Space direction="vertical" size={4}>
            <Text>
              {String(wordsError?.message || "error")}
              {statusCode ? ` (HTTP ${statusCode})` : ""}
            </Text>
            <Text type="secondary">{hint}</Text>
          </Space>
        }
      />
    );
  }, [wordsError]);

  const wordsColumns = [
    {
      title: "Слово",
      dataIndex: "word",
      key: "word",
      render: (v) => <Text strong>{String(v || "")}</Text>,
    },
    {
      title: "Просрочки",
      dataIndex: "overdue_cnt",
      key: "overdue_cnt",
      width: 130,
      sorter: (a, b) => Number(a.overdue_cnt || 0) - Number(b.overdue_cnt || 0),
      render: (v) => <Tag color="volcano">{Number(v || 0)}</Tag>,
    },
    {
      title: "Закрытые",
      dataIndex: "answered_cnt",
      key: "answered_cnt",
      width: 120,
      sorter: (a, b) => Number(a.answered_cnt || 0) - Number(b.answered_cnt || 0),
      render: (v) => <Tag color="green">{Number(v || 0)}</Tag>,
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      width: 110,
      sorter: (a, b) => Number(a.score || 0) - Number(b.score || 0),
      render: (v) => <Tag>{Number(v || 0).toFixed(2)}</Tag>,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card size="small" style={panelCardStyle} bodyStyle={{ padding: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Space align="center" size={8}>
                <Badge status="error" />
                <Text strong style={{ fontSize: 20 }}>
                  Просроченные SLA
                </Text>
              </Space>
              <Text type="secondary">
                Список просрочек, ручные действия по кейсам и аналитика слов, которые чаще всего остаются без реакции.
              </Text>
            </Space>
          </Col>

          <Col>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (activeTab === "analysis") loadWords();
                  else loadList();
                }}
                loading={activeTab === "analysis" ? loadingWords : loadingList}
              >
                Обновить
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div style={{ height: 12 }} />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "list",
            label: (
              <Space>
                <WarningOutlined />
                Список
              </Space>
            ),
            children: (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={6}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="Открытых" value={metrics.openCount} prefix={<ClockCircleOutlined />} />
                    </Card>
                  </Col>
                  <Col xs={24} md={6}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="Просрочено" value={metrics.overdueCount} prefix={<WarningOutlined />} />
                    </Card>
                  </Col>
                  <Col xs={24} md={6}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="Средняя просрочка" value={fmtDuration(metrics.avgOverdueSec)} />
                    </Card>
                  </Col>
                  <Col xs={24} md={6}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="Макс. просрочка" value={fmtDuration(metrics.maxOverdueSec)} />
                    </Card>
                  </Col>
                </Row>
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={8}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="🔥 Critical SLA" value={quickMetrics.criticalCount} />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="🟠 High SLA" value={quickMetrics.highCount} />
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" style={metricCardStyle}>
                      <Statistic title="🟡 Medium SLA" value={quickMetrics.mediumCount} />
                    </Card>
                  </Col>
                </Row>

                <Card size="small" style={panelCardStyle} bodyStyle={{ padding: 16 }}>
                  <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
                    <Text strong>Фильтры и список просрочек</Text>
                    <Text type="secondary">Отфильтруйте звезду, статус, лимит и найдите нужный кейс по тексту или chat_id.</Text>
                  </Space>
                  <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Звезда</Text>
                      <Select style={{ width: "100%" }} value={star} options={STAR_OPTIONS} onChange={setStar} />
                    </Col>

                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Статус</Text>
                      <Select style={{ width: "100%" }} value={status} options={STATUS_OPTIONS} onChange={setStatus} />
                    </Col>

                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Лимит</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={limit}
                        options={[
                          { value: 25, label: "25" },
                          { value: 50, label: "50" },
                          { value: 100, label: "100" },
                          { value: 200, label: "200" },
                        ]}
                        onChange={setLimit}
                      />
                    </Col>

                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Поиск (чат / текст)</Text>
                      <Input
                        ref={searchRef}
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="например: бонусы / -100..."
                        onChange={(e) => debouncedSetSearch(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: "12px 0" }} />

                  {listTopAlert}

                  <Table
                    rowKey={(r) => String(r?.id ?? `${r?.source_chat_id || ""}:${r?.incoming_message_id || ""}`)}
                    loading={loadingList}
                    columns={columns.map((c) =>
                      c.key === "actions" ? { ...c, fixed: "right" } : c
                    )}
                    dataSource={items}
                    size="middle"
                    pagination={false}
                    scroll={{ x: 1200, y: 620 }}
                    sticky
                    rowClassName={(record, index) => {
                      const due = Number(record?.due_ts || 0);
                      const overdueSec = due ? Math.max(0, nowTs - due) : 0;
                      const zebra = index % 2 === 0 ? "sla-row-even" : "sla-row-odd";
                      if (overdueSec >= 3600) return `${zebra} sla-row-critical`;
                      if (overdueSec >= 1800) return `${zebra} sla-row-high`;
                      if (overdueSec >= 600) return `${zebra} sla-row-medium`;
                      return zebra;
                    }}
                    expandable={{
                      expandedRowRender,
                      expandRowByClick: true,
                    }}
                    locale={{
                      emptyText: (
                        <Empty
                          description={
                            listError
                              ? "Нет данных (ошибка загрузки)"
                              : "Пока нет просроченных SLA по выбранным фильтрам"
                          }
                        />
                      ),
                    }}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: "analysis",
            label: (
              <Space>
                <BarChartOutlined />
                Аналитика
              </Space>
            ),
            children: (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Card size="small" style={panelCardStyle} bodyStyle={{ padding: 16 }}>
                  <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
                    <Text strong>Аналитика проблемных слов</Text>
                    <Text type="secondary">Слова с высоким score чаще встречаются в кейсах, которые зависают без реакции.</Text>
                  </Space>
                  <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Звезда</Text>
                      <Select style={{ width: "100%" }} value={star} options={STAR_OPTIONS} onChange={setStar} />
                    </Col>

                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Период</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={hours}
                        options={PERIOD_OPTIONS}
                        onChange={(v) => {
                          setRange(null);
                          setHours(v);
                        }}
                        disabled={!!range}
                      />
                    </Col>

                    <Col xs={24} md={8}>
                      <Text type="secondary" style={filterLabelStyle}>Или точный диапазон</Text>
                      <DatePicker.RangePicker style={{ width: "100%" }} showTime onChange={(v) => setRange(v)} allowClear />
                    </Col>

                    <Col xs={24} md={4}>
                      <Text type="secondary" style={filterLabelStyle}>Топ</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={top}
                        options={[
                          { value: 20, label: "20" },
                          { value: 30, label: "30" },
                          { value: 50, label: "50" },
                        ]}
                        onChange={setTop}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[12, 12]} align="middle" style={{ marginTop: 10 }}>
                    <Col xs={24} md={6}>
                      <Text type="secondary" style={filterLabelStyle}>Мин. длина слова</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={minLen}
                        options={[
                          { value: 2, label: "2" },
                          { value: 3, label: "3" },
                          { value: 4, label: "4" },
                          { value: 5, label: "5" },
                        ]}
                        onChange={setMinLen}
                      />
                    </Col>

                    <Col xs={24} md={18}>
                      <Alert
                        type="info"
                        showIcon
                        message="Как читать score"
                        description={
                          <span>
                            Score показывает “насколько слово чаще встречается в просрочках” относительно закрытых кейсов.
                            Чем выше score — тем больше вероятность, что это “плохой” паттерн для реакции.
                          </span>
                        }
                        style={{ borderRadius: 12 }}
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: "12px 0" }} />

                  {wordsTopAlert}

                  <Table
                    rowKey={(r) => String(r?.word ?? r?.id ?? `${r?.word || ""}:${r?.score || ""}`)}
                    loading={loadingWords}
                    columns={wordsColumns}
                    dataSource={words}
                    size="middle"
                    pagination={false}
                    locale={{
                      emptyText: (
                        <Empty
                          description={
                            wordsError ? "Нет данных (ошибка загрузки)" : "Нет данных для анализа по выбранным фильтрам"
                          }
                        />
                      ),
                    }}
                  />

                  <Divider style={{ margin: "12px 0" }} />

                  <Space>
                    <Button
                      onClick={() => {
                        try {
                          const lines = (words || []).slice(0, 30).map((w) => {
                            const word = String(w.word || "");
                            const o = Number(w.overdue_cnt || 0);
                            const a = Number(w.answered_cnt || 0);
                            const s = Number(w.score || 0).toFixed(2);
                            return `${word}\t${o}\t${a}\t${s}`;
                          });
                          const text = ["word\toverdue\tanswered\tscore", ...lines].join("\n");
                          navigator.clipboard.writeText(text);
                          message.success("Скопировано (таблица слов) ✅");
                        } catch {
                          message.error("Не удалось скопировать");
                        }
                      }}
                    >
                      Скопировать топ-слова
                    </Button>
                  </Space>
                </Card>
              </Space>
            ),
          },
        ]}
      />

      <Divider style={{ margin: "14px 0" }} />

      <Card size="small" style={panelCardStyle} bodyStyle={{ padding: 14 }}>
        <Space direction="vertical" size={6}>
          <Tag>Текущий фильтр: {STATUS_OPTIONS.find((x) => x.value === status)?.label || status}</Tag>
          <Tag color="red">1ч+: {quickMetrics.criticalCount}</Tag>
          <Tag color="volcano">30м+: {quickMetrics.highCount}</Tag>
          <Tag color="gold">10м+: {quickMetrics.mediumCount}</Tag>
          <Text type="secondary">
            Примечание: если видите HTTP 404 по /api/sla/overdue или /api/sla/overdue/words — значит эти эндпоинты ещё не добавлены на сервер.
          </Text>
          <Text type="secondary">
            Если видите HTTP 401 — Mini App открыт не внутри Telegram, или initData не передаётся.
          </Text>
        </Space>
      </Card>
    <style>
    {`
.sla-row-even {
  background: #ffffff;
}

.sla-row-odd {
  background: #fafafa;
}

.ant-table-row:hover .ant-btn {
  opacity: 1;
}

.ant-table-row .ant-btn {
  opacity: 0.85;
}

.sla-row-critical td {
  background: #fff1f0 !important;
}

.sla-row-high td {
  background: #fff7e6 !important;
}

.sla-row-medium td {
  background: #fffbe6 !important;
}

.ant-table-wrapper .ant-table-sticky-holder {
  z-index: 3;
}
    `}
    </style>
    </div>
  );
}